import { Directive, OnDestroy } from '@angular/core';
import { BehaviorSubject, MonoTypeOperatorFunction, Observable, ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Directive()
export class DestroyableBase implements OnDestroy {
  private readonly autoDisposeSubjectList: Array<Subject<any>> = [];
  private readonly onDestroySubject$ = new ReplaySubject<void>(1);
  private _isDestroyed = false;

  protected getLifecycleAwareSubject$<T>(): Subject<T> {
    const subject$ = new Subject<T>();
    this.autoDisposeSubjectList.push(subject$);
    return subject$;
  }

  protected getLifecycleAwareBehaviorSubject$<T>(initialValue: T): BehaviorSubject<T> {
    const subject$ = new BehaviorSubject<T>(initialValue);
    this.autoDisposeSubjectList.push(subject$);
    return subject$;
  }

  protected getLifecycleAwareReplaySubject$<T>(replayBufferSize = 1): ReplaySubject<T> {
    const subject$ = new ReplaySubject<T>(replayBufferSize);
    this.autoDisposeSubjectList.push(subject$);
    return subject$;
  }

  get onDestroy$(): Observable<void> {
    return this.onDestroySubject$.asObservable();
  }

  protected untilDestroy$<T>(obs$: Observable<T>): Observable<T> {
    return obs$.pipe(takeUntil(this.onDestroySubject$));
  }

  public ngOnDestroy(): void {
    this.destroy();
  }

  public takeUntilDestroyed<T>(): MonoTypeOperatorFunction<T> {
    return takeUntil<T>(this.onDestroy$);
  }

  private destroy() {
    this._isDestroyed = true;
    this.onDestroySubject$.next();
    this.onDestroySubject$.complete();

    this.autoDisposeSubjectList.forEach((subject$) => {
      if (!subject$.closed) {
        subject$.complete();
      }
    });
  }

  get isDestroyed(): boolean {
    return this._isDestroyed;
  }
}
